import React, { useContext, useState, useMemo } from 'react';
import clsx from 'clsx';

import { AccessibilityDetailsProps, PropertyDetailsProps, KeyProps } from '../HotelInformation.types';
import { Styled } from './index.styles';
import { serverData } from '../HotelInformation';
import { usePageContext } from '../../../context/PageContext';
import { hotelInformationMockData } from '../mock/hotelInformation';
import { useCheckBreakpoint } from '../../../hooks';
import { clickTrackingLoc } from '../constants/ApplicationConstants';

import { formatPhoneNumber } from '../../../utils';
import { useHQVContext } from '../../../context';
import { useSearchResultsStore } from '../../../modules/store/searchResultsStore';

export const PropertyDetails: React.FC = () => {
  const { jsonContent } = useContext(serverData);
  // const { jsonContent, generalInformationData } = useContext(serverData);
  const { pageState, currentLocale, resolvedUrl, isAuthorMode } = usePageContext();
  const { hqvState } = useHQVContext();
  const isViewportL = useCheckBreakpoint('viewportL');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const accessibilityQueryData = useSearchResultsStore((state: { propertyInfoData: any }) => state.propertyInfoData);

  const accessibilityData = useMemo(() => {
    return pageState?.authorMode || isAuthorMode
      ? hotelInformationMockData.hotelInformationData
      : accessibilityQueryData?.propertyInfoData;
  }, [pageState?.authorMode, accessibilityQueryData, isAuthorMode]);

  const allAmenities = accessibilityData?.property?.facilitiesAndServices;
  const accessibilityAmenities: AccessibilityDetailsProps = {};
  const phoneNumber = accessibilityData?.property?.contactInformation?.contactNumbers[0]?.phoneNumber?.display;
  const keys: KeyProps = {};
  allAmenities?.forEach((amenity: PropertyDetailsProps) => {
    if (amenity?.type?.description === 'Accessibility') {
      const key = amenity.groupId;
      if (key && amenity?.groupName) {
        if (!(key in accessibilityAmenities)) {
          accessibilityAmenities[key] = [];
          keys[key] = amenity?.groupName;
        }
        accessibilityAmenities[key].push(amenity.description);
      }
    }
  });
  const [isAccessibilityOpen, setIsAccessibilityOpen] = useState(false);
  const hideBorder = resolvedUrl?.includes('/availabilityCalendar') ? true : false;

  return accessibilityData ? (
    <Styled>
      <div
        id="hqv-property-details-section"
        className={clsx(
          'property-details-component',
          'adjustable-margin-element',
          `${hqvState.modalOpenState ? 'hqv-modal' : ''}`
        )}
      >
        <h2 className={clsx(isViewportL ? 't-title-s' : 't-subtitle-xl', 'component-heading')}>
          {jsonContent?.propertyDetailsLabel}
        </h2>
        <div className="component-content">
          <div className={clsx('col-12', 'p-0', 'accordion', 'm-accordion', 'accordian')} id="accordionExample3">
            <div className={clsx('accordion-item', 'accordian-item')}>
              <h2 className="accordion-header" id="accessibilityInfoHeading">
                <button
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#accessibilityInfo"
                  aria-expanded={isAccessibilityOpen ? 'true' : 'false'}
                  aria-controls="accessibilityInfo"
                  tabIndex={0}
                  className={clsx(
                    't-font-m',
                    'accordion-button',
                    'accordian-title',
                    'custom_click_track',
                    isAccessibilityOpen ? '' : 'collapsed'
                  )}
                  onClick={(): void => {
                    setIsAccessibilityOpen(!isAccessibilityOpen);
                  }}
                  onKeyDown={(event): void => {
                    if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
                      event.preventDefault();
                      setIsAccessibilityOpen(!isAccessibilityOpen);
                    }
                  }}
                  {...{
                    custom_click_track_value: `${clickTrackingLoc}| See accessibility details clicked |internal`,
                  }}
                >
                  <div className="m-accordion-title">
                    <p className={clsx('t-font-m', 'accordian-heading')}>
                      {jsonContent?.accessibilityInformationLabel}
                    </p>
                  </div>
                </button>
              </h2>
              <div
                id="accessibilityInfo"
                className={clsx(
                  'accordion-collapse',
                  'collapse',
                  'accordian-content',
                  isAccessibilityOpen ? 'show' : 'hide-content'
                )}
                aria-labelledby="accessibilityInfoHeading"
                data-bs-parent="#accordionExample3"
              >
                <p className={clsx(isViewportL ? 't-font-s' : 't-font-xs', 'accordion-body', 'accessibility-note')}>
                  {`${jsonContent?.moreInformationLabel} ${formatPhoneNumber(phoneNumber, currentLocale)}`}
                </p>
                {Object.keys(keys).map(key => (
                  <div key={key}>
                    <div className={clsx('t-subtitle-m', 'subheading-title')}>{keys[key]}</div>
                    <div className="subheading-content">
                      {accessibilityAmenities[key].map(accessibilityFeature => (
                        <div
                          key={accessibilityFeature}
                          className={clsx(isViewportL ? 't-font-s' : 't-font-xs', 'accessibility-item')}
                        >
                          {accessibilityFeature}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {!hideBorder && <div className="add-border" />}
            </div>
          </div>
        </div>
      </div>
    </Styled>
  ) : null;
};
